import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Lock, Smartphone, Loader2, Tablet, Check, LogOut, User } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useTabletKiosk } from "@/hooks/useTabletKiosk";
import { useTabletWorkSession } from "@/hooks/useTabletWorkSession";
import { useAuth } from "@/hooks/useAuth";
import { PublicRestaurant } from "@/hooks/usePublicMenu";
import { TabletSession, useTabletSession } from "@/hooks/useTabletSession";
import { TabletTableSelector } from "./TabletTableSelector";
import { InstallButton } from "@/components/pwa/InstallButton";
import { toast } from "sonner";

interface TabletSetupScreenProps {
  restaurant: PublicRestaurant;
  tables: Array<{ id: string; number: string; name: string | null; status: string }>;
  onSetupComplete: (session: TabletSession) => void;
}

export function TabletSetupScreen({
  restaurant,
  tables,
  onSetupComplete,
}: TabletSetupScreenProps) {
  const { user, signIn, signOut, loading: authLoading } = useAuth();
  const { checkinWithCode, requestApproval, isPending, cancelPendingRequest, session, hasActiveSession, isLoading: sessionLoading } =
    useTabletWorkSession(restaurant.id);
  const { setTabletSession } = useTabletSession();
  const kiosk = useTabletKiosk();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTable, setSelectedTable] = useState<string>("");

  // Kiosk mode (device-local)
  const [enableKiosk, setEnableKiosk] = useState(kiosk.enabled);
  const [kioskPin, setKioskPin] = useState("");

  // Determine current step based on state
  const getCurrentStep = (): "login" | "auth" | "table" => {
    if (hasActiveSession) return "table";
    if (!user) return "login";
    return "auth";
  };

  const step = getCurrentStep();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    setIsSubmitting(true);
    const { error } = await signIn(email.trim(), password);
    setIsSubmitting(false);

    if (error) {
      if (error.message.includes("Invalid login credentials")) {
        toast.error("E-mail ou senha incorretos");
      } else {
        toast.error("Erro ao fazer login");
      }
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setEmail("");
    setPassword("");
    setCode("");
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || code.length < 6) return;

    setIsSubmitting(true);
    const success = await checkinWithCode(code.trim());
    setIsSubmitting(false);
  };

  const handleRequestApproval = async () => {
    setIsSubmitting(true);
    await requestApproval();
    setIsSubmitting(false);
  };

  const handleCancelRequest = async () => {
    setIsSubmitting(true);
    await cancelPendingRequest();
    setIsSubmitting(false);
  };

  const handleTableSelect = (tableNumber: string) => {
    setSelectedTable(tableNumber);
  };

  const handleConfirmSetup = () => {
    if (!selectedTable) {
      toast.error("Selecione uma mesa");
      return;
    }
    
    if (!session) {
      toast.error("Sessão de trabalho não encontrada. Tente novamente.");
      return;
    }

    if (enableKiosk) {
      const result = kiosk.enable(kioskPin);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
    } else {
      kiosk.disable();
    }

    const tabletSessionData: TabletSession = {
      waiterSessionId: session.id,
      tableNumber: selectedTable,
      operatorName: "Tablet",
      setupAt: new Date().toISOString(),
      restaurantId: restaurant.id,
    };

    setTabletSession(tabletSessionData);
    onSetupComplete(tabletSessionData);
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Step 3: Select table
  if (step === "table") {
    if (sessionLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
        <Card className="w-full max-w-4xl bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-primary/20 flex items-center justify-center">
              <Tablet className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl text-white">
              Selecione a Mesa deste Tablet
            </CardTitle>
            <p className="text-slate-400 mt-2">
              Escolha qual mesa este tablet irá atender hoje
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <TabletTableSelector
              tables={tables}
              selectedTable={selectedTable}
              onSelect={handleTableSelect}
            />

            <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Modo Kiosk (recomendado)</Label>
                  <p className="text-sm text-slate-400">
                    Bloqueia acesso às configurações e exige PIN para sair.
                  </p>
                </div>
                <Switch checked={enableKiosk} onCheckedChange={setEnableKiosk} />
              </div>

              {enableKiosk && (
                <div className="space-y-2">
                  <Label className="text-slate-300">PIN de saída (4 a 8 dígitos)</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    placeholder="••••"
                    value={kioskPin}
                    onChange={(e) => setKioskPin(e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500">
                    Este PIN fica salvo apenas neste tablet (armazenamento local do navegador).
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-center pt-4">
              <Button
                size="lg"
                className="min-w-[200px] h-14 text-lg"
                disabled={!selectedTable || !session || sessionLoading || (enableKiosk && kioskPin.trim().length < 4)}
                onClick={handleConfirmSetup}
              >
                {sessionLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Check className="h-5 w-5 mr-2" />
                )}
                Iniciar Atendimento
              </Button>
            </div>

            <Separator className="bg-slate-700" />

            <div className="text-center text-sm text-slate-400">
              <p>📱 Tablet de Autoatendimento</p>
              <p>📍 {restaurant.name}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Waiting for approval
  if (isPending) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Loader2 className="h-10 w-10 text-amber-500 animate-spin" />
            </div>
            <CardTitle className="text-2xl text-white">Aguardando Aprovação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-center text-slate-400">
              Sua solicitação foi enviada para o PDV. Aguarde a aprovação.
            </p>

            <div className="flex justify-center">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                </span>
                Aguardando...
              </div>
            </div>

            <div className="text-center text-sm text-slate-400">
              <p>👤 {user?.email}</p>
            </div>

            <Button
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={handleCancelRequest}
              disabled={isSubmitting}
            >
              Cancelar Solicitação
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="w-full text-slate-500 hover:text-slate-300"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 1: Login
  if (step === "login") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            {restaurant.logo_url && (
              <img
                src={restaurant.logo_url}
                alt={restaurant.name}
                className="w-24 h-24 rounded-full object-cover mx-auto mb-4 ring-4 ring-primary/30"
              />
            )}
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl text-white">Login do Operador</CardTitle>
            <p className="text-slate-400 text-sm mt-2">
              Faça login para configurar o tablet
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">
                  E-mail
                </label>
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">
                  Senha
                </label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  disabled={isSubmitting}
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-lg"
                disabled={!email.trim() || !password.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                Entrar
              </Button>
            </form>

            <Separator className="bg-slate-700" />

            <div className="text-center text-sm text-slate-400">
              <p>📍 {restaurant.name}</p>
            </div>

            <div className="pt-2">
              <InstallButton 
                variant="subtle" 
                className="w-full text-slate-400 hover:text-slate-200 hover:bg-slate-700/50" 
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Authentication (code or approval)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          {restaurant.logo_url && (
            <img
              src={restaurant.logo_url}
              alt={restaurant.name}
              className="w-24 h-24 rounded-full object-cover mx-auto mb-4 ring-4 ring-primary/30"
            />
          )}
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl text-white">Configurar Tablet</CardTitle>
          <p className="text-slate-400 text-sm mt-2">
            Faça o check-in para configurar este tablet
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Code input */}
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">
                Código do Dia
              </label>
              <Input
                type="text"
                placeholder="______"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="text-center text-3xl tracking-[0.5em] font-mono uppercase bg-slate-900/50 border-slate-600 text-white h-16"
                disabled={isSubmitting}
              />
              <p className="text-xs text-slate-500 text-center">
                Peça o código ao gerente ou operador de caixa
              </p>
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-lg"
              disabled={code.length < 6 || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : null}
              Validar Código
            </Button>
          </form>

          <div className="relative">
            <Separator className="bg-slate-700" />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 px-3 text-xs text-slate-500">
              ou
            </span>
          </div>

          {/* Request approval */}
          <Button
            variant="outline"
            className="w-full h-12 border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={handleRequestApproval}
            disabled={isSubmitting}
          >
            <Smartphone className="h-5 w-5 mr-2" />
            Solicitar Liberação ao PDV
          </Button>

          <Separator className="bg-slate-700" />

          <div className="text-center text-sm text-slate-400">
            <p>👤 {user?.email}</p>
            <p>📍 {restaurant.name}</p>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="w-full text-slate-500 hover:text-slate-300"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>

          <div className="pt-2">
            <InstallButton 
              variant="subtle" 
              className="w-full text-slate-400 hover:text-slate-200 hover:bg-slate-700/50" 
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
